import { useCallback } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { clearAllOfflineStores } from "@/offline/db";

/**
 * Single sign-out implementation for every avatar menu in the app.
 *
 * Order matters here:
 * 1. `queryClient.clear()` synchronously drops every cached query (identity
 *    probe, projects list, records, plans, current user). Without this the
 *    in-memory QueryClient still holds the previous user's data and the
 *    persister would re-dehydrate it to IDB on the next tick. The Sentry user
 *    is detached in the same synchronous step so the next person's events on a
 *    shared device are never attributed to the signed-out user.
 * 2. `clearAllOfflineStores()` wipes the IDB layer — `identity`,
 *    `pinnedProjects`, `assets`, and the `queryClientCache` snapshot.
 * 3. `signOut()` revokes the WorkOS session. May redirect; may reject when
 *    offline. Either way, steps 1 and 2 already cleaned local state.
 *
 * The WorkOS phase re-throws so callers (`UserMenu`, `ProjectHeader`) can
 * recover a stuck "Signing out…" overlay. Both wipes log to Sentry but
 * don't block — even partial cleanup is better than skipping it entirely.
 *
 * Two avatar menus call this — `UserMenu` (project list) and `ProjectHeader`
 * (per-project pages). Keep this hook the single source of truth: if a new
 * sign-out surface lands, it must use this hook so the local wipe never
 * gets skipped on a multi-user device.
 */
export function useSignOut() {
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(async () => {
    queryClient.clear();
    // Detach the signed-out user from Sentry's global scope immediately, so a
    // shared-device sign-out can't leave the previous user attached to the next
    // person's events — independent of the post-redirect reload. AuthGuard's
    // useSentryUser also clears reactively; this is the deterministic teardown.
    Sentry.getGlobalScope().setUser(null);
    try {
      await clearAllOfflineStores();
    } catch (err) {
      Sentry.captureException(err, {
        tags: { area: "offline-init", phase: "signout-clear" },
      });
    }
    try {
      await signOut();
    } catch (err) {
      Sentry.captureException(err, {
        tags: { area: "offline-init", phase: "workos-signout" },
      });
      throw err;
    }
  }, [signOut, queryClient]);
}
